import {
    contextRepository,
    driveRepository,
    eventRepository,
    fragmentRepository,
    gameRepository,
    playerRepository,
    playRepository,
    statsRepository,
    streamRepository,
    teamRepository,
    transactionManager
} from "../../storage/storage";
import { SyncService } from "./sync-service";
import GameService from "../game-service";
import FootballBoxscoreService from "../boxscores/football-boxscore-service";
import PersistenceError from "../exceptions/persistence-error";
import StreamService from "../stream-service";
import { normalizeLegacyDrives } from "./normalize-legacy-drives";
import { recoverUpCountingClockData } from "../../sports/football/utils/up-clock-recovery";

class FootballSyncService extends SyncService {
    /**
     * Replace a local event using remote data, attempt to map and match remote player and team id's to the already present local id's.
     * @param {string} eventId Local eventId
     * @returns {Promise<null | {game: FootballGame, gameContext: FootballGameContext}>}
     */
    async overWriteEventData(eventId) {
        const localEvent = await eventRepository.getById(eventId);
        const localGame = await gameRepository
            .where({ eventId: eventId })
            .first();
        if (localGame) await GameService.deleteGame(localGame.id);
        await StreamService.deleteStreams(localEvent.id);
        const eventData = await this.fetchEventData(localEvent.eventId);
        if (eventData && Object.keys(eventData).length) {
            const {
                game,
                gameContext,
                stats,
                event,
                players,
                awayTeam,
                homeTeam,
                streams
            } = eventData;

            // Strip legacy Start of Drive plays from remote payloads so synced
            // games match the implicit-drive model (idempotent for new games).
            const { plays, fragments, drives } = normalizeLegacyDrives(
                eventData.plays,
                eventData.fragments,
                eventData.drives
            );

            const {
                mergedAwayTeam,
                mergedHomeTeam,
                updateMap,
                mergedContext,
                mergedPlayers,
                mergedEvent,
                mergedGame,
                mergedStreams
            } = await this._mergeEvent(
                event,
                awayTeam,
                homeTeam,
                players,
                gameContext,
                game,
                streams
            );

            const mergedStats = this._getMergedStats(stats, updateMap);
            const mergedFragments = this._getMergedFragments(
                fragments,
                updateMap
            );
            const mergedPlays = [...this._getMergedPlays(plays, updateMap)];
            const mergedDrives = this._getMergedDrives(drives, updateMap);

            // Convert plays recorded under an up-counting clock (pushed by
            // devices on 0.11.7–0.11.9 with ascending Clock Type) back to
            // canonical remaining time before persisting. Idempotent.
            const recovery = recoverUpCountingClockData({
                plays: mergedPlays,
                fragments: mergedFragments,
                drives: mergedDrives
            });
            if (recovery.converted) {
                console.warn(
                    `Recovered up-counting clock data on sync for event ${eventId}: ` +
                        `${recovery.convertedPlayIds.length} plays converted` +
                        (recovery.skippedDriveIds.length
                            ? `; drives needing manual review: ${recovery.skippedDriveIds.join(
                                  ", "
                              )}`
                            : "")
                );
            }

            try {
                await transactionManager.transaction(
                    "rw",
                    [
                        gameRepository,
                        eventRepository,
                        playRepository,
                        fragmentRepository,
                        driveRepository,
                        contextRepository,
                        statsRepository,
                        teamRepository,
                        playerRepository,
                        streamRepository
                    ],
                    async () => {
                        await Promise.all([
                            gameRepository.put(mergedGame),
                            eventRepository.put(mergedEvent),
                            playRepository.bulkPut(mergedPlays),
                            fragmentRepository.bulkPut(mergedFragments),
                            driveRepository.bulkPut(mergedDrives),
                            contextRepository.put(mergedContext),
                            statsRepository.bulkPut(mergedStats),
                            teamRepository.bulkPut([
                                mergedAwayTeam,
                                mergedHomeTeam
                            ]),
                            playerRepository.bulkPut(mergedPlayers),
                            streamRepository.bulkPut(mergedStreams)
                        ]);
                    }
                );
            } catch (err) {
                console.error(
                    "Error saving remote event: ",
                    eventData,
                    err.stack
                );
                throw new PersistenceError(
                    "Remote event synchronization failed"
                );
            }

            await this._overWriteSync(mergedEvent.id, eventData);

            return {
                game: await gameRepository.getById(game.id),
                gameContext: await contextRepository.getById(gameContext.id)
            };
        }

        return null;
    }

    async collectEventData(gameId) {
        const game = await gameRepository.getById(gameId);
        const event = await eventRepository.getById(game.eventId);
        const { awayTeam, homeTeam } = event.teams;
        const plays = await playRepository.getPlaysWithFragments(gameId);
        const fragments = plays.map((play) => play.fragments).flat();
        const drives = await driveRepository.where({ gameId }).toArray();
        const context = await contextRepository.where({ gameId }).first();
        const stats = await statsRepository.where({ gameId }).toArray();
        const awayPlayers = await playerRepository
            .where({ teamId: awayTeam.id })
            .toArray();
        const homePlayers = await playerRepository
            .where({ teamId: homeTeam.id })
            .toArray();
        const streams = await streamRepository
            .where({ eventId: event.id })
            .toArray();

        const eventData = {
            timeSynced: new Date().toISOString(),
            plays: plays.map((play) => play.toJSON()),
            fragments: fragments.map((fragment) => fragment.toJSON()),
            game: game.toJSON(),
            gameContext: context.toJSON(),
            drives: drives.map((drive) => drive.toJSON()),
            players: [
                ...awayPlayers.map((player) => player.toJSON()),
                ...homePlayers.map((player) => player.toJSON())
            ],
            stats: stats.map((stat) => stat.toJSON()),
            event: event.toJSON(),
            awayTeam: awayTeam.toJSON?.() || awayTeam,
            homeTeam: homeTeam.toJSON?.() || homeTeam,
            streams: streams.map((stream) => stream.toJSON())
        };

        return eventData;
    }

    /**
     * @param {{[p:string]:*}[]} remoteStats
     * @param {{[p:string]:string}} updateMap
     * @returns {FootballStats[]}
     * @private
     */
    _getMergedStats(remoteStats, updateMap) {
        return this._updateProps(remoteStats, updateMap).map((stat) =>
            statsRepository.mapToEntity(stat)
        );
    }

    /**
     * @param {{[p:string]: *}[]} remoteFragments
     * @param {{[p:string]:string}} updateMap
     * @returns {Fragment[]}
     * @private
     */
    _getMergedFragments(remoteFragments, updateMap) {
        return this._updateProps(remoteFragments, updateMap).map((fragment) =>
            fragmentRepository.mapToEntity(fragment)
        );
    }

    /**
     * @param {{[p:string]:*}[]} remotePlays
     * @param {{[p:string]: string}} updateMap
     * @returns {(FootballPlay|FlagFootballPlay)[]}
     * @private
     */
    _getMergedPlays(remotePlays, updateMap) {
        return this._updateProps(remotePlays, updateMap).map((play) =>
            playRepository.mapToEntity(play)
        );
    }

    /**
     * @param {{[p:string]:*}[]} remoteDrives
     * @param {{[p:string]: string}} updateMap
     * @returns {Drive[]}
     * @private
     */
    _getMergedDrives(remoteDrives, updateMap) {
        return this._updateProps(remoteDrives, updateMap).map((drive) =>
            driveRepository.mapToEntity(drive)
        );
    }

    async _performSecondarySync({ id: gameId }) {
        try {
            await FootballBoxscoreService.saveBoxscoreByGameId(gameId);
        } catch (e) {
            console.error("Boxscore could not be submitted");
        }
    }
}

export default new FootballSyncService();
